import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { mapPromise } from '@rad/utility/promise';
import { useFetch, useLocalStorage, usePromise } from '@rad/hooks';
import { useNotify } from '@rad/contexts/notifications';
import createTable from '@rad/components/ui/table';
import { staticForm, formValues, formWithErrors, unsafeMapForm } from '@rad/components/dynamic-form/common';
import { getScorecard, createScorecard, updateScorecard, removeScorecardMeasure, getLookups, lookupMeta } from '../types';
import ActionButton from '../components/action-button';
import LayoutRoot from '../layouts/root';
import LayoutForm from '../layouts/form';
const Targets = (props) => {
    return _jsxs(_Fragment, { children: [props.measure.threshold || _jsx(_Fragment, { children: "\u2013" }), " / ", props.measure.target || _jsx(_Fragment, { children: "\u2013" }), " / ", props.measure.max || _jsx(_Fragment, { children: "\u2013" })] });
};
const Actions = (props) => {
    if (!props.canEdit)
        return null;
    return _jsxs(_Fragment, { children: [_jsx(ActionButton, { href: `#/scorecards/${props.measure.scorecard_id}/measures/${props.measure.id}/edit`, style: "danger", icon: "pencil", title: "Edit" }), _jsx(ActionButton, { style: "danger", icon: "trash", title: "Remove", onClick: async () => {
                    if (confirm(`Remove ${props.measure.measure.display} from this scorecard?`)) {
                        await removeScorecardMeasure(props.measure);
                        props.reload();
                    }
                } })] });
};
const lookupOptions = (lookups, kind, filter = () => true, selected) => Promise.resolve([
    { id: '', label: 'Select one...' },
    ...lookups.filter((x) => x.kind === kind && (filter(x) || x.value === selected)).map((x) => ({ id: x.value, label: x.title })),
    ...(selected && !lookups.some((x) => x.kind === kind && x.value === selected) ? [{ id: selected, label: selected }] : []),
]);
const configureLookupFields = (form, lookups) => {
    const values = formValues(form);
    const program = lookups.find((x) => x.kind === 'program' && x.value === values.program);
    const period = program && lookupMeta(program).period;
    const hasPeriod = typeof period === 'string' && !!period.trim();
    return unsafeMapForm(form, (field) => {
        if (field.id === 'program')
            return { ...field, settings: { ...field.settings, source: { id: null, options: lookupOptions(lookups, 'program', () => true, values.program) } } };
        if (field.id === 'context')
            return { ...field, settings: { ...field.settings, source: { id: null, options: lookupOptions(lookups, 'scorecard-context', () => true, values.context) } } };
        if (field.id === 'timeframe')
            return { ...field, disabled: !hasPeriod, settings: { ...field.settings,
                    tooltip: hasPeriod ? undefined : 'Select a Program with a Period configured to choose a Timeframe.',
                    source: {
                        id: null,
                        options: lookupOptions(lookups, 'timeframe', (x) => hasPeriod && lookupMeta(x).active !== false && lookupMeta(x).period === period, values.timeframe),
                    } } };
        return field;
    });
};
const updateLookupForm = (current, next, lookups) => {
    const previousProgram = formValues(current).program;
    const nextProgram = formValues(next).program;
    const adjusted = previousProgram !== nextProgram
        ? unsafeMapForm(next, (field) => field.id === 'timeframe' ? { ...field, value: '' } : field)
        : next;
    const programMeta = lookupMeta(lookups.find((x) => x.kind === 'program' && x.value === nextProgram) || { kind: '', value: '', title: '', meta: '{}' });
    const withProgramDefaults = previousProgram !== nextProgram
        ? unsafeMapForm(adjusted, (field) => field.id === 'context' ? { ...field, value: programMeta.context || '' } : field)
        : adjusted;
    return configureLookupFields(withProgramDefaults, lookups);
};
const adminFields = ['forced_potential_points', 'max_bonus_physician', 'max_bonus_apc'];
const visibleForm = (form, isAdmin) => ({ ...form, fields: form.fields.filter((f) => isAdmin || !f((x) => adminFields.includes(x.id))) });
const constructForm = (lookups = []) => configureLookupFields(staticForm([
    [
        {
            'id': 'title',
            'title': 'Title',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'plain'
            },
            'required': true,
        },
        {
            'id': 'owner',
            'title': 'Owner',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'email'
            },
            'required': true,
        },
        {
            'id': 'program',
            'title': 'Program',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': { 'id': null, 'options': lookupOptions(lookups, 'program') }
            },
            'required': true,
        },
        {
            'id': 'timeframe',
            'title': 'Timeframe',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': { 'id': null, 'options': lookupOptions(lookups, 'timeframe', () => false) }
            },
            'required': true,
        },
        {
            'id': 'context',
            'title': 'Context',
            'kind': 'select',
            'value': '',
            'settings': { 'source': { 'id': null, 'options': lookupOptions(lookups, 'scorecard-context') } },
            'required': false,
        },
        {
            'id': 'context_filter',
            'title': 'Context Filter',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'multiline' },
            'required': false,
        },
        {
            'id': 'filter',
            'title': 'Filter',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'multiline' },
            'required': false,
        },
        {
            'id': 'forced_potential_points',
            'title': 'Forced Potential Points',
            'kind': 'number',
            'value': null,
            'settings': {},
            'required': false,
        },
    ],
    [
        {
            'id': 'max_bonus_physician',
            'title': 'Max Bonus Physician',
            'kind': 'number',
            'value': null,
            'settings': {},
            'required': false,
        },
        {
            'id': 'max_bonus_apc',
            'title': 'Max Bonus APC',
            'kind': 'number',
            'value': null,
            'settings': {},
            'required': false,
        },
    ],
]), lookups);
const ScorecardNew = (props) => {
    const [token] = useLocalStorage('token');
    const notify = useNotify();
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise);
    const [stateForm, setForm] = React.useState(constructForm());
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await createScorecard({
            ...formValues(visibleForm(stateForm, token?.is_admin === true)),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Scorecard created successfully!' });
            window.location.hash = `#/`;
        }
        else {
            setErrors(result);
        }
    };
    React.useEffect(() => {
        if (lookups)
            setForm((form) => configureLookupFields(form, lookups));
    }, [lookups]);
    return _jsx(LayoutForm, { title: "New Scorecard", form: formWithErrors(visibleForm(stateForm, token?.is_admin === true), stateErrors), onChange: (form) => setForm((current) => updateLookupForm(current, form, lookups || [])), onSubmit: submit });
};
const ScorecardEdit = (props) => {
    const [token] = useLocalStorage('token');
    const notify = useNotify();
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise);
    const [scorecardPromise] = useFetch(getScorecard(props.id));
    const scorecard = usePromise(scorecardPromise);
    const [stateForm, setForm] = React.useState(constructForm());
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await updateScorecard({
            'id': props.id,
            ...formValues(visibleForm(stateForm, token?.is_admin === true)),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Scorecard updated successfully!' });
            window.location.hash = `#/`;
        }
        else {
            setErrors(result);
        }
    };
    React.useEffect(() => {
        setForm((current) => configureLookupFields(unsafeMapForm(current, (x) => {
            if (!!scorecard) {
                switch (x.id) {
                    case 'title': return { ...x, 'value': scorecard.title };
                    case 'owner': return { ...x, 'value': scorecard.owner };
                    case 'program': return { ...x, 'value': scorecard.program };
                    case 'timeframe': return { ...x, 'value': scorecard.timeframe };
                    case 'context': return { ...x, 'value': scorecard.context || '' };
                    case 'context_filter': return { ...x, 'value': scorecard.context_filter || '' };
                    case 'filter': return { ...x, 'value': scorecard.filter || '' };
                    case 'forced_potential_points': return { ...x, 'value': scorecard.forced_potential_points };
                    case 'max_bonus_physician': return { ...x, 'value': scorecard.max_bonus_physician };
                    case 'max_bonus_apc': return { ...x, 'value': scorecard.max_bonus_apc };
                    default: return x;
                }
            }
            else {
                return x;
            }
        }), lookups || []));
    }, [scorecard, lookups]);
    return _jsx(LayoutForm, { title: _jsxs(_Fragment, { children: ["Edit Scorecard ", scorecard && _jsx("em", { children: scorecard.display })] }), form: formWithErrors(visibleForm(stateForm, token?.is_admin === true), stateErrors), onChange: (form) => setForm((current) => updateLookupForm(current, form, lookups || [])), onSubmit: submit });
};
const ScorecardView = (props) => {
    const [token] = useLocalStorage('token');
    const [scorecardPromise, scorecardReload] = useFetch(getScorecard(props.id));
    const scorecard = usePromise(scorecardPromise);
    const DataTable = createTable(mapPromise(scorecardPromise, (x) => x?.measures || []));
    return _jsx(LayoutRoot, { toolbarRight: _jsxs(_Fragment, { children: [token?.is_admin && _jsx("a", { href: `#/audit-log/scorecard/${props.id}`, className: "btn btn-sm btn-outline-secondary", children: "View Audit Log" }), scorecard && scorecard.is_build && _jsx("a", { href: `#/scorecards/${props.id}/measures/new`, className: "btn btn-sm btn-primary ms-1", children: "Add Measure" })] }), children: _jsx(DataTable, { title: _jsxs(_Fragment, { children: ["Measures on Scorecard ", scorecard && _jsx("em", { children: scorecard.display })] }), columns: [
                { 'header': 'Measure', 'render': (x) => _jsxs(_Fragment, { children: [x.title, " (", x.measure.display, ")"] }) },
                { 'header': 'Weight', 'render': (x) => x.weight || _jsx(_Fragment, { children: "\u2013" }),
                    'footer': (xs) => xs.reduce((agg, x) => agg + (x.weight || 0), 0),
                    'footerClassName': (xs) => xs.reduce((agg, x) => agg + (x.weight || 0), 0) === 100 ? '' : 'fw-bold text-danger', },
                { 'header': 'Targets', 'render': (x) => _jsx(Targets, { measure: x }) },
                { 'header': '', 'render': (x) => _jsx(Actions, { measure: x, canEdit: scorecard?.is_build === true, reload: scorecardReload }), 'className': 'w-1' }
            ] }) });
};
export const ScorecardScreen = (props) => props.id && props.edit ? _jsx(ScorecardEdit, { id: props.id }) : props.id ? _jsx(ScorecardView, { id: props.id }) : _jsx(ScorecardNew, {});
export default ScorecardScreen;
